import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';
import type { ParseScheduleResponse } from './types';

/**
 * Query keys invalidated after a schedule rule changes. A schedule rule backs
 * both exploratory and suite scheduling surfaces, plus the run lists they feed,
 * so we refresh all of them. Invalidating a key with no active query is a no-op.
 */
const SCHEDULE_QUERY_KEYS = [
  ['exploratory-schedules'],
  ['exploration-schedules'],
  ['suite-schedules'],
  ['exploratory-tests'],
  ['workflow-test-executions'],
] as const;

const invalidateScheduleQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  for (const queryKey of SCHEDULE_QUERY_KEYS) {
    queryClient.invalidateQueries({ queryKey });
  }
};

/** Parse a natural-language schedule into a cron + concise English description. */
export function useParseSchedule() {
  return useMutation({
    mutationFn: async (text: string): Promise<ParseScheduleResponse> => {
      const response = await fetch(`${API_URL}/org/automation/parse-schedule`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (!response.ok) {
        throw await toApiError(response, 'Could not interpret that schedule');
      }
      return response.json();
    },
  });
}

/** Pause/resume a schedule rule (toggle enabled). */
export function useToggleScheduleRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: string) => {
      const response = await fetch(`${API_URL}/org/automation/rules/${ruleId}/toggle`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to update schedule');
      }
      return response.json();
    },
    onSuccess: () => invalidateScheduleQueries(queryClient),
  });
}

/** Trigger a schedule's run immediately ("run now"). */
export function useRunScheduleNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: string) => {
      const response = await fetch(`${API_URL}/org/automation/rules/${ruleId}/run`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to start run');
      }
      return response.json();
    },
    onSuccess: () => invalidateScheduleQueries(queryClient),
  });
}

/** Delete a schedule rule. */
export function useDeleteScheduleRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: string) => {
      const response = await fetch(`${API_URL}/org/automation/rules/${ruleId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        throw await toApiError(response, 'Failed to delete schedule');
      }
      return response.json();
    },
    onSuccess: () => invalidateScheduleQueries(queryClient),
  });
}
